import {Vue, Mixins, Component, Ref} from 'vue-property-decorator';
import NoticeCategoryData from '@/components/board/mixins/NoticeCategoryData';
import FaqCategoryData from '@/components/board/mixins/FaqCategoryData';
import {SHOPBY_ACCESSTOKEN} from '@/const/words/cookie';

import {toI18n} from '@/utils/commonUtils';
import {SearchTypeData, FilterDateData, FilterDateMapping, BoardRespones} from '@/types';

import {deleteCookie} from '@/utils/cookieUtils';
import {changeDateFormat, momentAdd, addMonth} from '@/utils/dateUtils';
import PageDateRangePicker from '@/components/board/common/PageDateRangePicker.vue';

interface RequestParams {
  [propName: string]: any;
}

@Component
export default class BoardMixin extends Mixins(NoticeCategoryData, FaqCategoryData) {
  @Ref()
  protected readonly dateRangePickerRef!: PageDateRangePicker;

  public get endDateTime(): string {
    return this.requestParams.endDateTime;
  }

  public get startDateTime(): string {
    return this.requestParams.startDateTime;
  }

  public threeMonthsAgo: string = addMonth(new Date(), -3);
  public today: string = changeDateFormat('', 'YYYY-MM-DD');

  public searchTypeData: SearchTypeData[] = [
    {value: 'ALL', label: this.toI18n('BOARD_FAQ_ALL')}, // 전체
    {value: 'TITLE', label: this.toI18n('BOARD_FAQ_TITLE')}, // 제목
    {value: 'CONTENT', label: this.toI18n('BOARD_FAQ_CONTENTS')} // 내용
  ];

  public filterDateData: FilterDateData[] = [
    {value: '0', label: this.toI18n('BOARD_FAQ_TODAY'), selected: false}, // 오늘
    {value: '1w', label: this.toI18n('BOARD_FAQ_1WEEK'), selected: false}, // 1주일
    {value: '1m', label: this.toI18n('BOARD_FAQ_1MONTH'), selected: false}, // 1개월
    {value: '3m', label: this.toI18n('BOARD_FAQ_3MONTHS'), selected: true}, // 3개월
    {value: '6m', label: this.toI18n('BOARD_FAQ_6MONTHS'), selected: false}, // 6개월
    {value: '1y', label: this.toI18n('BOARD_FAQ_1YEAR'), selected: false} // 1년
  ];

  public filterDateMapping: FilterDateMapping = {
    '0': [0, 'd'],
    '1w': [-7, 'd'],
    '1m': [-1, 'M'],
    '3m': [-3, 'M'],
    '6m': [-6, 'M'],
    '1y': [-1, 'y']
  };

  public searchResult: BoardRespones = {
    content: [],
    pageable: {},
    totalPages: 0,
    totalElements: 0,
    last: true,
    number: 1,
    sort: {},
    size: 30,
    first: false,
    numberOfElements: 0,
    empty: true
  };

  public requestParams: RequestParams = {};

  public get currentDateRange(): string {
    const selectVal = this.filterDateData.find((data: any): any => data.selected) || {
      value: '0',
      label: this.toI18n('BOARD_FAQ_TODAY'),
      selected: true
    };
    return selectVal.value;
  }

  public dateData: {startYmd: string; endYmd: string} = {
    startYmd: '',
    endYmd: this.today
  };
  public dateReceiveData = {...this.dateData};

  public setStartYmd(): void {
    if (this.dateReceiveData.endYmd) {
      Object.assign(this.dateData, {
        startYmd: momentAdd(this.dateReceiveData.endYmd, this.filterDateMapping[this.currentDateRange], 'YYYY-MM-DD'),
        endYmd: this.dateReceiveData.endYmd
      });
    } else {
      Object.assign(this.dateData, {
        startYmd: momentAdd(this.today, this.filterDateMapping[this.currentDateRange], 'YYYY-MM-DD'),
        endYmd: this.today
      });
    }

    this.dateReceiveData = {...this.dateData};
  }

  public setYmd(): void {
    this.dateData = {...this.dateReceiveData};
    if (this.currentDateRange === '0') {
      Object.assign(this.dateData, {
        startYmd: this.today,
        endYmd: this.today
      });
    } else if (this.currentDateRange === 'ALL') {
      Object.assign(this.dateData, {
        startYmd: '2000-01-01',
        endYmd: this.today
      });
    } else {
      this.setStartYmd();
    }
    this.dateReceiveData = {...this.dateData};
  }

  public setFilterUnSelected(): void {
    const oldIdx = this.filterDateData.findIndex((item: any): any => item.selected);
    Vue.set(this.filterDateData, oldIdx, {...this.filterDateData[oldIdx], selected: false});
  }

  public dateFilterChange(idx: number): void {
    this.setFilterUnSelected();
    Vue.set(this.filterDateData, idx, {...this.filterDateData[idx], selected: true});

    // 퀵버튼을 클릭했을 때 종료일을 항상 오늘 기준으로 세팅
    this.dateReceiveData = {
      ...this.dateReceiveData,
      endYmd: this.today
    }

    this.setYmd();
  }

  public dateDataChange({startYmd, endYmd, isBtnSet}: any): void {
    if (!isBtnSet) {
      this.setFilterUnSelected();
      // 퀵버튼을 클릭하면 이벤트 실행 순서가 dateFilterChange -> dateDataChange 순서로 실행되기 때문에
      // 퀵버튼으로 날짜를 변경하여도 이전 날짜로 세팅되는 이슈가 있어 퀵버튼이 아닌 경우에만 dateReceiveData 를 갱신하도록 코드 옮김
      this.dateReceiveData = {startYmd, endYmd};
    }
    // 변경된 날짜로 업데이트를 하지 않으면, 수동으로 종료 일자를 선택 한 후, 오늘 버튼을 클릭하면 시작 일자가 변경되지 않음
    this.dateRangePickerRef.onDateObjectChange(this.dateReceiveData);
  }

  public toI18n(needToI18nStr: string): string {
    return toI18n(this.$t(needToI18nStr));
  }

  public exceptionHandler(e: any): void {
    const matchCode: string[] | null = e.toString().match(/status\s*\d+/i);
    if (matchCode) {
      const [, code] = matchCode[0].split(' ');
      if (code === '401') {
        deleteCookie(SHOPBY_ACCESSTOKEN);
        this.$router.push('/');
      }
    } else if (typeof e === 'object') {
      const {code} = e;
      if (code === 'TOKEN_ERROR') {
        deleteCookie(SHOPBY_ACCESSTOKEN);
        this.$router.push('/');
      }
    }
  }
}
